import React, { useState } from 'react';
import {
    Box,
    Button,
    Chip,
    FormControl,
    InputLabel,
    makeStyles,
    MenuItem,
    Select,
    TextField,
    Typography,
} from '@material-ui/core';

import { applyCustomerGuaranteeDaysToAll } from 'components/catalog/utils/customerGuaranteeDaysByCompanySource';

const useStyles = makeStyles({
    root: {
        width: '100%',
        textAlign: 'left',
        // Align with Material TextField label/input baseline in the adjacent vendor column.
        paddingTop: 2,
    },
    row: {
        display: 'flex',
        alignItems: 'flex-end',
        flexWrap: 'wrap',
        gap: 12,
        width: '100%',
    },
    companySourceSelect: {
        flex: '1 1 180px',
        minWidth: 160,
    },
    daysInput: {
        flex: '0 0 112px',
        width: 112,
        '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
            margin: 0,
            WebkitAppearance: 'none',
        },
        '& input[type=number]': {
            MozAppearance: 'textfield',
        },
    },
});

/**
 * Compact CGD editor: pick a company source, set days, see all values as chips.
 * Always visible so the field remains discoverable even with 0 company sources.
 *
 * @param {Object} props
 * @param {number[]} props.companySourceIDs
 * @param {Array<{id: number, displayName?: string}>} props.companySourceOptions
 * @param {Record<string, string>} props.valuesByCompanySourceID
 * @param {(next: Record<string, string>) => void} props.onChange
 * @param {boolean} [props.disabled]
 * @param {boolean} [props.showError]
 */
function CustomerGuaranteeDaysByCompanySourceFields({
    companySourceIDs,
    companySourceOptions,
    disabled = false,
    onChange,
    showError = false,
    valuesByCompanySourceID,
}) {
    const classes = useStyles();
    const selectionKey = companySourceIDs.join(',');
    const [activeCompanySourceID, setActiveCompanySourceID] = useState(() => companySourceIDs[0] ?? null);
    const [syncedSelectionKey, setSyncedSelectionKey] = useState(selectionKey);

    // Keep the active source valid when the selected company-source set changes (no mirroring effect).
    if (selectionKey !== syncedSelectionKey) {
        setSyncedSelectionKey(selectionKey);
        const nextActive =
            activeCompanySourceID != null && companySourceIDs.includes(activeCompanySourceID)
                ? activeCompanySourceID
                : (companySourceIDs[0] ?? null);
        if (nextActive !== activeCompanySourceID) {
            setActiveCompanySourceID(nextActive);
        }
    }

    const optionsById = new Map(companySourceOptions.map((source) => [source.id, source]));
    const getDisplayName = (id) => optionsById.get(id)?.displayName || `Company source ${id}`;

    const hasSources = companySourceIDs.length > 0;
    const activeID = hasSources
        ? companySourceIDs.includes(activeCompanySourceID)
            ? activeCompanySourceID
            : companySourceIDs[0]
        : null;
    const activeValue = activeID != null ? (valuesByCompanySourceID[String(activeID)] ?? '') : '';
    const activeFieldError = showError && hasSources && activeValue === '';
    const daysDisabled = disabled || !hasSources;
    const canApplyToAll = !disabled && companySourceIDs.length > 1 && activeValue !== '';
    const hasMissingValue = companySourceIDs.some((id) => {
        const value = valuesByCompanySourceID[String(id)];
        return value == null || value === '';
    });
    const filledCount = companySourceIDs.filter((id) => {
        const value = valuesByCompanySourceID[String(id)];
        return value != null && value !== '';
    }).length;

    return (
        <Box className={classes.root}>
            <Box display="flex" alignItems="baseline" flexWrap="wrap" style={{ gap: 8 }}>
                <Typography variant="subtitle2">Customer Guarantee Days</Typography>
                <Typography variant="caption" color="textSecondary">
                    {filledCount}/{companySourceIDs.length} company sources set
                </Typography>
            </Box>

            <Box className={classes.row} mt={0.5}>
                <FormControl className={classes.companySourceSelect} disabled={disabled || !hasSources}>
                    <InputLabel id="CustomerGuaranteeDays_companySource_label" shrink>
                        Company source
                    </InputLabel>
                    <Select
                        labelId="CustomerGuaranteeDays_companySource_label"
                        id="CustomerGuaranteeDays_companySource"
                        value={activeID ?? ''}
                        displayEmpty
                        onChange={(event) => setActiveCompanySourceID(Number(event.target.value))}
                    >
                        {!hasSources ? (
                            <MenuItem value="" disabled>
                                Select a company source above
                            </MenuItem>
                        ) : (
                            companySourceIDs.map((id) => (
                                <MenuItem key={id} value={id}>
                                    {getDisplayName(id)}
                                </MenuItem>
                            ))
                        )}
                    </Select>
                </FormControl>

                <TextField
                    id="CustomerGuaranteeDays_days"
                    className={classes.daysInput}
                    type="number"
                    value={activeValue}
                    required={hasSources}
                    disabled={daysDisabled}
                    error={activeFieldError}
                    placeholder="Days"
                    inputProps={{
                        min: 0,
                        step: 1,
                        inputMode: 'numeric',
                        'aria-label':
                            activeID != null
                                ? `Customer Guarantee Days — ${getDisplayName(activeID)}`
                                : 'Customer Guarantee Days',
                    }}
                    onChange={(event) => {
                        if (activeID == null) {
                            return;
                        }
                        onChange({
                            ...valuesByCompanySourceID,
                            [String(activeID)]: event.target.value,
                        });
                    }}
                />

                {canApplyToAll ? (
                    <Button
                        color="primary"
                        size="small"
                        onClick={() => onChange(applyCustomerGuaranteeDaysToAll(valuesByCompanySourceID, activeID))}
                    >
                        {`Apply ${activeValue} to all`}
                    </Button>
                ) : null}
            </Box>

            {hasSources ? (
                <Box mt={1.5} display="flex" flexWrap="wrap" style={{ gap: 8 }}>
                    {companySourceIDs.map((id) => {
                        const value = valuesByCompanySourceID[String(id)];
                        const hasValue = value != null && value !== '';
                        const isActive = id === activeID;
                        const chipError = showError && !hasValue;

                        return (
                            <Chip
                                key={id}
                                size="small"
                                clickable={!disabled}
                                color={isActive ? 'primary' : 'default'}
                                variant={isActive || hasValue ? 'default' : 'outlined'}
                                label={hasValue ? `${getDisplayName(id)}: ${value}` : `${getDisplayName(id)}: —`}
                                onClick={() => {
                                    if (!disabled) {
                                        setActiveCompanySourceID(id);
                                    }
                                }}
                                style={chipError ? { borderColor: '#f44336', color: '#f44336' } : undefined}
                            />
                        );
                    })}
                </Box>
            ) : null}

            {showError && !hasSources ? (
                <Box mt={1}>
                    <Typography variant="caption" color="error" display="block">
                        Select a company source, then enter customer guarantee days.
                    </Typography>
                </Box>
            ) : null}
            {showError && hasSources && hasMissingValue ? (
                <Box mt={1}>
                    <Typography variant="caption" color="error" display="block">
                        Enter customer guarantee days for every company source.
                    </Typography>
                </Box>
            ) : null}
        </Box>
    );
}

export { CustomerGuaranteeDaysByCompanySourceFields };
